import React from "react";
import { NavLink, Outlet, useNavigate, useMatch } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SIDEBAR, ROLE_LABEL, SUB_ROLE_LABEL, STAGE_LABEL, filterSidebar, packedStageOverride } from "@/constants/roles";
import { LogOut, Bell, Search, ChevronRight, ChevronDown, ChevronsDownUp, ChevronsUpDown, Sun, Moon, X, Menu, Package, Users as UsersIcon, Boxes, ClipboardList, CornerDownLeft,
  LayoutDashboard, Inbox, Clock, FileCheck, Factory, ArrowLeftRight, Truck, PackageCheck, CheckCircle2, HelpCircle, Flag, XCircle, Warehouse, Container, Settings, Receipt, Sparkles, Ban, Hammer, PanelLeft, ChartColumn, Siren, Wrench } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { SearchProvider, useSearch } from "@/contexts/SearchContext";
import ViewportModeToggle from "@/components/ViewportModeToggle";
import { useT } from "@/i18n/LanguageContext";
import { alertHeadline } from "@/i18n/alerts";

// Sidebar nav item — renders a plain NavLink OR a collapsible group when the
// item has children. The group header is itself a NavLink (so clicking the
// label still navigates to the parent list, e.g. "Pre Orders"), and a small
// chevron toggles the sub-items on/off. Auto-expands when a child route is
// active so the nested item is visible without an extra click.
// ---------------------------------------------------------------------------
// Sidebar styles — user-selectable from the top bar (persisted in localStorage).
//   compact · black rail, as wide as its longest label (default)
//   light   · light-grey rail, red active item
// "Fold" is a separate toggle (top bar + sidebar footer): Fold = every
// sub-menu hidden (the parent lights red on a sub-page); Unfold = every
// sub-menu open. Stays as set until pressed again.
// ---------------------------------------------------------------------------
export const SIDEBAR_STYLES = [
  { key: "compact", label: "Compact", title: "Black sidebar (default)" },
  { key: "light", label: "Light", title: "Light-grey sidebar" },
];
const SidebarStyleContext = React.createContext({ style: "compact", setStyle: () => {}, folded: false, setFolded: () => {} });
export const useSidebarStyle = () => React.useContext(SidebarStyleContext);

const NAV_ICONS = {
  dashboard: LayoutDashboard, customers: UsersIcon, orders_pre_order: Inbox, orders_approved_leads: Sparkles, orders_abandoned: Ban,
  orders_pending: Clock, orders_advance: FileCheck, orders_no_mold: Hammer, orders_inventory_packing: Boxes, orders_production: Factory, orders_pickup_3pl: Warehouse,
  orders_product_transfer: ArrowLeftRight, orders_packing: Package, orders_ready_to_ship: PackageCheck, orders_shipping: Truck,
  orders_completed: ClipboardList, orders_fulfilled: CheckCircle2, orders_na: HelpCircle, orders_flagged: Flag, orders_cancelled: XCircle,
  inventory: Warehouse, batch_orders: Container, billing: Receipt, team: UsersIcon, users: UsersIcon, settings: Settings, report: ChartColumn,
  rush: Siren, installers: Wrench,
};
const NavIcon = ({ item, size = 16, className = "" }) => {
  const Icon = NAV_ICONS[item.key] || Package;
  return <Icon size={size} className={className} />;
};

function SidebarNavItem({ item, extraActive, light = false, folded = true }) {
  const { t } = useT();
  const location = window.location.pathname;
  const isChildActive = (item.children || []).some((c) => location === c.to || c.to === extraActive);
  // Fold → all groups closed · Unfold → all groups open. The chevron still
  // toggles one group locally until the next Fold/Unfold press.
  const [open, setOpen] = React.useState(!folded);
  React.useEffect(() => { setOpen(!folded); }, [folded]);
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  // Parent lights up when a hidden child is the current page.
  const parentForceActive = item.to === extraActive || (isChildActive && !open);
  const idle = light ? "text-slate-700 hover:text-slate-900 hover:bg-white/70" : "text-neutral-400 hover:text-white hover:bg-white/5";
  const idleChild = light ? "text-slate-600 hover:text-slate-900 hover:bg-white/70" : "text-neutral-400 hover:text-white hover:bg-white/5";

  return (
    <div className="mb-0.5">
      <div className="relative">
        <NavLink
          to={item.to}
          end={item.to === "/"}
          data-testid={`nav-${item.to.replace(/\//g, "-")}`}
          className={({ isActive }) => {
            const active = isActive || parentForceActive;
            return `group flex items-center gap-2 ${hasChildren ? "pr-8" : "pr-2"} pl-2.5 py-1.5 rounded-md text-[13px] transition-colors ${
              active ? "text-white" : idle
            }`;
          }}
          style={({ isActive }) => ((isActive || parentForceActive) ? { background: "var(--brand-red)" } : {})}
        >
          <NavIcon item={item} size={15} className="shrink-0 opacity-80" />
          <span className="whitespace-nowrap flex-1">{t(item.label)}</span>
          {!hasChildren && (
            <ChevronRight size={12} className="opacity-0 group-hover:opacity-60 transition-opacity" />
          )}
        </NavLink>
        {hasChildren && (
          <button
            type="button"
            aria-label={open ? `Collapse ${t(item.label)}` : `Expand ${t(item.label)}`}
            data-testid={`nav-toggle-${item.to.replace(/\//g, "-")}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
            className={`absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded ${light ? "hover:bg-white/70 text-slate-500" : "hover:bg-white/10 text-neutral-300"}`}
          >
            <ChevronDown size={14} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
          </button>
        )}
      </div>
      {hasChildren && open && (
        <div className={`ml-4 mt-0.5 border-l pl-2 ${light ? "border-slate-300" : "border-white/10"}`}>
          {item.children.map((c) => {
            const childForceActive = c.to === extraActive;
            return (
              <NavLink
                key={c.to}
                to={c.to}
                data-testid={`nav-${c.to.replace(/\//g, "-")}`}
                className={({ isActive }) => {
                  const active = isActive || childForceActive;
                  return `group flex items-center justify-between px-2 py-1 rounded-md text-[12px] mb-0.5 transition-colors ${
                    active ? "text-white" : idleChild
                  }`;
                }}
                style={({ isActive }) => ((isActive || childForceActive) ? { background: "var(--brand-red)" } : {})}
              >
                <span className="whitespace-nowrap">{t(c.label)}</span>
                <ChevronRight size={10} className="opacity-0 group-hover:opacity-60 transition-opacity" />
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Top-bar switcher: one Dark/Light toggle + Fold toggle. */
function SidebarStyleToggle() {
  const { t } = useT();
  const { style, setStyle, folded, setFolded } = useSidebarStyle();
  const light = style === "light";
  const btn = (on) => `px-2 py-0.5 rounded text-[11px] font-semibold transition-colors ${on ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`;
  return (
    <div className="flex items-center gap-0.5 border border-slate-200 rounded-md px-0.5 py-0.5" data-testid="sidebar-style-toggle" title="Sidebar">
      <PanelLeft size={13} className="text-slate-400 ml-1" />
      <button
        type="button"
        onClick={() => setStyle(light ? "compact" : "light")}
        title={light ? t("Switch side menu to dark") : t("Switch side menu to light")}
        data-testid="sidebar-theme-toggle"
        aria-pressed={light}
        className={`${btn(false)} inline-flex items-center gap-1`}
      >
        {light ? <Moon size={11} /> : <Sun size={11} />} {light ? t("Dark") : t("Light")}
      </button>
      <span className="w-px h-3.5 bg-slate-200 mx-0.5" />
      <button
        type="button"
        onClick={() => setFolded((v) => !v)}
        title={folded ? t("Unfold menus — show every sub-menu (e.g. Settings → Users, Team)") : t("Fold menus — show only the main items")}
        data-testid="sidebar-style-fold"
        data-state={folded ? "folded" : "unfolded"}
        aria-pressed={!folded}
        className={`${btn(!folded)} inline-flex items-center gap-1`}
      >
        {folded ? <ChevronsUpDown size={11} /> : <ChevronsDownUp size={11} />} {folded ? t("Unfold") : t("Fold")}
      </button>
    </div>
  );
}

/**
 * Language toggle in the header — flips between English and 简体中文.
 * Session-only (no persistence): resets to English on every reload,
 * per user's explicit request Feb 2026.
 */
function LanguageToggle() {
  const { lang, setLang } = useT();
  const isZh = lang === "zh";
  return (
    <button
      type="button"
      onClick={() => setLang(isZh ? "en" : "zh")}
      data-testid="lang-toggle"
      title={isZh ? "Switch to English" : "切换到简体中文"}
      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-100 text-[12px] font-semibold text-slate-700 transition-colors"
    >
      <span className={isZh ? "opacity-40" : "text-slate-900"} data-testid="lang-en">EN</span>
      <span className="text-slate-300">|</span>
      <span className={isZh ? "text-slate-900" : "opacity-40"} data-testid="lang-zh">中文</span>
    </button>
  );
}

// Font-size toggle — Small / Medium / Large / X-Large. Applies to the whole
// CRM by setting the CSS variable --user-font-scale on <html>. Persisted in
// localStorage so the user's choice sticks across reloads.
function FontSizeToggle() {
  const { t } = useT();
  const OPTS = [
    { key: "sm", label: "A-", scale: 0.9, title: t("Small") },
    { key: "md", label: "A", scale: 1.0, title: t("Medium (default)") },
    { key: "lg", label: "A+", scale: 1.12, title: t("Large") },
    { key: "xl", label: "A++", scale: 1.24, title: t("Extra Large") },
  ];
  const [value, setValue] = React.useState(() => {
    try { return window.localStorage.getItem("crm-font-scale") || "md"; } catch { return "md"; }
  });
  React.useEffect(() => {
    const opt = OPTS.find((o) => o.key === value) || OPTS[1];
    document.documentElement.style.setProperty("--user-font-scale", String(opt.scale));
    try { window.localStorage.setItem("crm-font-scale", value); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex items-center gap-0.5 border border-slate-200 rounded-md px-0.5 py-0.5" data-testid="font-scale-toggle">
      {OPTS.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => setValue(o.key)}
          title={o.title}
          data-testid={`font-scale-${o.key}`}
          className={`px-2 py-0.5 rounded font-semibold transition-colors ${value === o.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          style={{ fontSize: `${Math.min(o.scale * 12, 14)}px`, lineHeight: 1.2 }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}



function HeaderSearch() {
  const { query, setQuery, placeholder } = useSearch();
  const navigate = useNavigate();
  const { t } = useT();
  const { user } = useAuth();
  // Stations (factories, packing, shipping) search by order number only — no client names.
  const isStation = ["factory_1", "factory_2", "factory_3", "packing_orders", "inventory_packing", "shipping_order", "three_pl"].includes(user?.role);
  const [open, setOpen] = React.useState(false);
  const [activeIdx, setActiveIdx] = React.useState(0);
  const wrapRef = React.useRef(null);

  // Debounced query — only fetch after the user pauses typing.
  const [debounced, setDebounced] = React.useState("");
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 180);
    return () => clearTimeout(t);
  }, [query]);

  const shouldFetch = debounced.length >= 2;
  const { data, isFetching } = useQuery({
    queryKey: ["global-search", debounced],
    queryFn: async () => (await api.get(`/search/global`, { params: { q: debounced, limit: 8 } })).data,
    enabled: shouldFetch,
    staleTime: 15_000,
  });

  // Close dropdown on outside click.
  React.useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Flatten grouped results into a single navigable list (for keyboard arrows).
  const flat = React.useMemo(() => {
    const out = [];
    if (!data) return out;
    (data.orders || []).forEach((o) => out.push({ kind: "order", item: o }));
    (data.customers || []).forEach((c) => out.push({ kind: "customer", item: c }));
    (data.inventory || []).forEach((i) => out.push({ kind: "inventory", item: i }));
    (data.users || []).forEach((u) => out.push({ kind: "user", item: u }));
    return out;
  }, [data]);

  React.useEffect(() => { setActiveIdx(0); }, [debounced]);

  const goToResult = (entry) => {
    if (!entry) return;
    setOpen(false);
    setQuery("");
    if (entry.kind === "order") {
      navigate(`/order/${entry.item.id}`);
    } else if (entry.kind === "customer") {
      // No detail page — jump to Customers tab with the term seeded so their row highlights.
      navigate("/customers");
      setTimeout(() => setQuery(entry.item.name || entry.item.email || debounced), 60);
    } else if (entry.kind === "inventory") {
      navigate("/inventory");
      setTimeout(() => setQuery(entry.item.catalog_number || debounced), 60);
    } else if (entry.kind === "user") {
      navigate("/users");
      setTimeout(() => setQuery(entry.item.name || entry.item.email || debounced), 60);
    }
  };

  const onKeyDown = (e) => {
    if (!open || flat.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); goToResult(flat[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  // Build a running index so each row knows its position in the flat list (for arrow-key highlight).
  let runIdx = -1;
  const highlight = (text) => {
    if (!text || !debounced) return text || "";
    const t = String(text);
    const idx = t.toLowerCase().indexOf(debounced.toLowerCase());
    if (idx === -1) return t;
    return (
      <>
        {t.slice(0, idx)}
        <mark className="bg-yellow-200 text-neutral-900 rounded px-0.5">{t.slice(idx, idx + debounced.length)}</mark>
        {t.slice(idx + debounced.length)}
      </>
    );
  };

  const showDropdown = open && shouldFetch;
  const totalResults = flat.length;

  return (
    <div ref={wrapRef} className="relative flex-1 max-w-xl" data-testid="global-search">
      <div
        className="flex items-center gap-2 h-9 px-3 rounded-lg border transition-all focus-within:border-slate-400 focus-within:shadow-sm"
        style={{ background: "var(--brand-fog)", borderColor: "var(--brand-line)" }}
      >
        <Search size={15} className="text-neutral-400 shrink-0" />
        <input
          data-testid="header-search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="bg-transparent outline-none text-sm placeholder-neutral-400 w-full"
        />
        {query && (
          <button
            data-testid="header-search-clear"
            onClick={() => { setQuery(""); setOpen(false); }}
            className="text-neutral-400 hover:text-neutral-700 shrink-0"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          data-testid="global-search-dropdown"
          className="absolute left-0 right-0 top-11 z-[60] rounded-lg border bg-white shadow-2xl overflow-hidden max-h-[70vh] overflow-y-auto"
          style={{ borderColor: "var(--brand-line)" }}
        >
          <div className="flex items-center justify-between px-3 py-2 text-[11px] uppercase tracking-widest text-neutral-500 border-b border-slate-100">
            <span>{t("Global search")}</span>
            <span>
              {isFetching ? t("Searching…") : `${totalResults} ${totalResults === 1 ? t("result") : t("results")}`}
            </span>
          </div>

          {!isFetching && totalResults === 0 && (
            <div className="px-4 py-6 text-sm text-neutral-500 text-center">
              No matches for <span className="font-semibold text-neutral-700">"{debounced}"</span>.
            </div>
          )}

          {/* ORDERS */}
          {(data?.orders?.length || 0) > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-neutral-500 bg-slate-50 flex items-center gap-1.5">
                <Package size={11} /> {t("Orders")} <span className="text-neutral-400 font-normal">({data.orders.length})</span>
              </div>
              {data.orders.map((o) => {
                runIdx += 1;
                const active = runIdx === activeIdx;
                const idx = runIdx;
                const sub = [o.brand, o.model, o.year].filter(Boolean).join(" · ");
                const tag = packedStageOverride(o)?.label || STAGE_LABEL[o.status] || o.status;
                return (
                  <button
                    key={`o-${o.id}`}
                    type="button"
                    data-testid={`gs-order-${o.id}`}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => goToResult({ kind: "order", item: o })}
                    className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b border-slate-50 hover:bg-slate-50 ${active ? "bg-slate-50" : ""}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-900 truncate">
                        {highlight(o.po_number || o.shopify_order_number || o.catalog_number || "Untitled order")}
                        {!isStation && o.customer_name && <span className="ml-2 text-neutral-500 font-normal">— {highlight(o.customer_name)}</span>}
                      </div>
                      <div className="text-[11px] text-neutral-500 truncate">
                        {sub || "—"}
                        {o.vin && <> · VIN {highlight(o.vin)}</>}
                        {o.shipping_tracking_number && <> · Tracking {highlight(o.shipping_tracking_number)}</>}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-widest shrink-0">{tag}</Badge>
                    {active && <CornerDownLeft size={12} className="text-neutral-400 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* CUSTOMERS */}
          {(data?.customers?.length || 0) > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-neutral-500 bg-slate-50 flex items-center gap-1.5">
                <UsersIcon size={11} /> {t("Customers")} <span className="text-neutral-400 font-normal">({data.customers.length})</span>
              </div>
              {data.customers.map((c) => {
                runIdx += 1;
                const active = runIdx === activeIdx;
                const idx = runIdx;
                return (
                  <button
                    key={`c-${c.id}`}
                    type="button"
                    data-testid={`gs-customer-${c.id}`}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => goToResult({ kind: "customer", item: c })}
                    className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b border-slate-50 hover:bg-slate-50 ${active ? "bg-slate-50" : ""}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-900 truncate">{highlight(c.name || "Unnamed customer")}</div>
                      <div className="text-[11px] text-neutral-500 truncate">
                        {c.email && <>{highlight(c.email)}</>}
                        {c.phone && <> · {highlight(c.phone)}</>}
                        {c.company && <> · {highlight(c.company)}</>}
                      </div>
                    </div>
                    {active && <CornerDownLeft size={12} className="text-neutral-400 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* INVENTORY */}
          {(data?.inventory?.length || 0) > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-neutral-500 bg-slate-50 flex items-center gap-1.5">
                <Boxes size={11} /> {t("Inventory")} <span className="text-neutral-400 font-normal">({data.inventory.length})</span>
              </div>
              {data.inventory.map((i) => {
                runIdx += 1;
                const active = runIdx === activeIdx;
                const idx = runIdx;
                const sub = [i.brand, i.model, i.year, i.sub_trim || i.trim].filter(Boolean).join(" · ");
                return (
                  <button
                    key={`i-${i.id}`}
                    type="button"
                    data-testid={`gs-inv-${i.id}`}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => goToResult({ kind: "inventory", item: i })}
                    className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b border-slate-50 hover:bg-slate-50 ${active ? "bg-slate-50" : ""}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-900 truncate">
                        {highlight(i.catalog_number || "—")}
                        <span className="ml-2 text-neutral-500 font-normal">{sub}</span>
                      </div>
                      <div className="text-[11px] text-neutral-500 truncate">
                        {i.factory_1_code && <>F1 {highlight(i.factory_1_code)} </>}
                        {i.factory_2_code && <>· F2 {highlight(i.factory_2_code)} </>}
                        {typeof i.current_stock === "number" && <>· Stock {i.current_stock} </>}
                        {i.bin_location && <>· Bin {highlight(i.bin_location)}</>}
                      </div>
                    </div>
                    {active && <CornerDownLeft size={12} className="text-neutral-400 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* USERS */}
          {(data?.users?.length || 0) > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-neutral-500 bg-slate-50 flex items-center gap-1.5">
                <ClipboardList size={11} /> {t("Users")} <span className="text-neutral-400 font-normal">({data.users.length})</span>
              </div>
              {data.users.map((u) => {
                runIdx += 1;
                const active = runIdx === activeIdx;
                const idx = runIdx;
                return (
                  <button
                    key={`u-${u.id}`}
                    type="button"
                    data-testid={`gs-user-${u.id}`}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => goToResult({ kind: "user", item: u })}
                    className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b border-slate-50 hover:bg-slate-50 ${active ? "bg-slate-50" : ""}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-900 truncate">{highlight(u.name || "Unnamed user")}</div>
                      <div className="text-[11px] text-neutral-500 truncate">
                        {u.email && <>{highlight(u.email)}</>}
                        {(u.role || u.sub_role) && <> · {(ROLE_LABEL[u.role] || u.role)}{u.sub_role ? ` · ${SUB_ROLE_LABEL[u.sub_role] || u.sub_role}` : ""}</>}
                      </div>
                    </div>
                    {active && <CornerDownLeft size={12} className="text-neutral-400 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  return (
    <SearchProvider>
      <LayoutInner />
    </SearchProvider>
  );
}

// Slide-in drawer used when the app is in Tablet mode (the desktop sidebar
// is hidden via CSS). Opens on `crm-open-mobile-nav` event fired from the
// hamburger button in the header.
function MobileSidebar({ items, extraActive, user, onLogout }) {
  const [open, setOpen] = React.useState(false);
  const { t } = useT();
  const { folded, setFolded } = useSidebarStyle();
  const location = window.location.pathname;
  React.useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("crm-open-mobile-nav", openHandler);
    return () => window.removeEventListener("crm-open-mobile-nav", openHandler);
  }, []);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex" onClick={() => setOpen(false)} data-testid="mobile-nav">
      <div
        className="w-64 h-full text-neutral-200 flex flex-col"
        style={{ background: "var(--brand-ink)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/5 shrink-0">
          <div className="font-display font-bold text-sm text-white">Leprecon+</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setFolded((v) => !v)}
              data-testid="mobile-nav-fold"
              data-state={folded ? "folded" : "unfolded"}
              className="inline-flex items-center gap-1 h-7 px-2 rounded border border-white/20 text-[11px] font-semibold text-white/80 hover:bg-white/10"
              title={folded ? t("Unfold menus") : t("Fold menus")}
            >
              {folded ? <ChevronsUpDown size={12} /> : <ChevronsDownUp size={12} />} {folded ? t("Unfold") : t("Fold")}
            </button>
            <button className="text-white/70 hover:text-white" onClick={() => setOpen(false)} aria-label={t("Close")}>
              <X size={16} />
            </button>
          </div>
        </div>
        <nav className="py-3 px-2 flex-1 overflow-y-auto">
          {items.map((it) => {
            const kids = Array.isArray(it.children) ? it.children : [];
            const childActive = kids.some((c) => location === c.to || c.to === extraActive);
            const showKids = kids.length > 0 && (!folded || childActive);
            return (
            <React.Fragment key={it.to}>
              <NavLink
                to={it.to}
                end={it.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mnav-${it.to.replace(/\//g, "-")}`}
                className={({ isActive }) => {
                  const active = isActive || it.to === extraActive || (childActive && !showKids);
                  return `block px-3 py-2 rounded-md text-[13px] mb-0.5 ${
                    active ? "bg-white/10 text-white" : "text-neutral-300 hover:bg-white/5"
                  }`;
                }}
              >
                {t(it.label)}{kids.length > 0 && !showKids ? <span className="text-neutral-500 text-[11px]"> ▸ {kids.length}</span> : null}
              </NavLink>
              {showKids && kids.map((c) => (
                <NavLink
                  key={c.to}
                  to={c.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mnav-${c.to.replace(/\//g, "-")}`}
                  className={({ isActive }) => {
                    const active = isActive || c.to === extraActive;
                    return `block ml-5 pl-3 border-l border-white/10 py-1.5 rounded-md text-[12px] mb-0.5 ${
                      active ? "bg-white/10 text-white" : "text-neutral-400 hover:bg-white/5"
                    }`;
                  }}
                >
                  {t(c.label)}
                </NavLink>
              ))}
            </React.Fragment>
            );
          })}
        </nav>
        {user && (
          <div className="border-t border-white/5 p-3 shrink-0" data-testid="mobile-nav-user">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-white text-xs" style={{ background: "var(--brand-red)" }}>
                {user.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate text-white">{user.name}</div>
                <div className="text-[11px] text-neutral-400 truncate">{t(ROLE_LABEL[user.role])}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={onLogout}
              data-testid="mobile-logout-btn"
              className="mt-3 w-full h-10 rounded-md border border-white/20 text-sm font-semibold text-white hover:bg-white/10 flex items-center justify-center gap-2"
            >
              <LogOut size={15} /> {t("Log out")} · {t("Switch user")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Slim announcement bar pinned to the top of every page. Rotates through
// the open alerts feed one at a time (3-second dwell) so critical events
// (NA, Flagged, Reminders, Batch billing) surface from any tab. Clicking
// the current alert opens the linked order; hovering pauses the cycle so
// operators can read a long message before it flips.
function TopAlertStrip({ alerts }) {
  const navigate = useNavigate();
  const { t } = useT();
  const [idx, setIdx] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const open = React.useMemo(
    () => (Array.isArray(alerts) ? alerts.filter((a) => (a.status || "open") === "open") : []),
    [alerts],
  );
  React.useEffect(() => {
    if (idx >= open.length && open.length > 0) setIdx(0);
  }, [open.length, idx]);
  React.useEffect(() => {
    if (paused || open.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % open.length), 3000);
    return () => clearInterval(t);
  }, [open.length, paused]);
  if (open.length === 0) return null;
  const a = open[Math.min(idx, open.length - 1)];
  // Show only the headline — everything before the first ":", em-dash,
  // "period+space", or "|" (whichever comes first). Users asked to see
  // `Order PO-5046 moved to NA` and NOT the follow-up `: Marked NA via
  // FACTORY_1 tab '...' · fields: ...` details (Feb 28, 2026).
  const short = alertHeadline(a, t);
  const sev = a?.severity || "info";
  const bg = sev === "critical" ? "#7f1d1d" : sev === "warning" ? "#78350f" : "#1e3a8a";
  const dot = sev === "critical" ? "#ef4444" : sev === "warning" ? "#f59e0b" : "#3b82f6";
  const label = sev === "critical" ? t("CRITICAL") : sev === "warning" ? t("ATTENTION") : t("INFO");
  return (
    <div
      className="w-full text-white text-[13px] leading-tight flex-shrink-0"
      style={{ background: bg }}
      data-testid="top-alert-strip"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <button
        type="button"
        onClick={() => { if (a?.order_id) navigate(`/order/${a.order_id}`); }}
        className="w-full h-9 px-4 flex items-center gap-3 hover:opacity-90 transition-opacity text-left"
        data-testid="top-alert-strip-current"
      >
        <span
          className="inline-block w-2 h-2 rounded-full animate-pulse flex-shrink-0"
          style={{ background: dot }}
        />
        <span className="text-[10px] uppercase tracking-widest font-mono opacity-80 flex-shrink-0">
          {label}
        </span>
        <span className="truncate flex-1 font-medium">{short}</span>
        {open.length > 1 && (
          <span className="text-[10px] uppercase tracking-widest font-mono opacity-70 flex-shrink-0">
            {Math.min(idx, open.length - 1) + 1} / {open.length}
          </span>
        )}
      </button>
    </div>
  );
}


function LayoutInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useT();
  const items = filterSidebar(SIDEBAR[user.role] || [], user);

  const { data: alerts = [] } = useQuery({
    queryKey: ["alerts"],
    queryFn: async () => (await api.get("/alerts")).data,
    refetchInterval: 30000,
  });

  // When the user is on an order-detail page (`/order/:id`), keep the sidebar
  // stage tab that owns this order lit red — otherwise the highlight vanishes
  // the moment they click into a row. We reuse the same TanStack Query key as
  // OrderDetail (`["order", id]`) so no extra fetch fires; the detail page's
  // cache serves this consumer.
  const orderMatch = useMatch("/order/:id");
  const orderId = orderMatch?.params?.id;
  const { data: currentOrder } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => (await api.get(`/orders/${orderId}`)).data,
    enabled: !!orderId,
    staleTime: 10_000,
  });
  const extraActive = (orderId && currentOrder?.status)
    ? `/orders/${currentOrder.status}`
    : null;

  // Sidebar style (compact | light) + folded sub-menus — persisted per browser.
  const [sbStyle, setSbStyle] = React.useState(() => {
    try {
      const s = window.localStorage.getItem("crm-sidebar-style");
      return SIDEBAR_STYLES.some((o) => o.key === s) ? s : "compact";
    } catch { return "compact"; }
  });
  const [sbFolded, setSbFolded] = React.useState(() => {
    try { return window.localStorage.getItem("crm-sidebar-folded") !== "0"; } catch { return true; }
  });
  React.useEffect(() => { try { window.localStorage.setItem("crm-sidebar-style", sbStyle); } catch { /* ignore */ } }, [sbStyle]);
  React.useEffect(() => { try { window.localStorage.setItem("crm-sidebar-folded", sbFolded ? "1" : "0"); } catch { /* ignore */ } }, [sbFolded]);
  const light = sbStyle === "light";
  const asideBg = light ? "#E6E7EB" : "var(--brand-ink)";
  const asideText = light ? "text-slate-800" : "text-neutral-200";
  const divider = light ? "border-slate-300" : "border-white/5";

  return (
    <SidebarStyleContext.Provider value={{ style: sbStyle, setStyle: setSbStyle, folded: sbFolded, setFolded: setSbFolded }}>
    <div className="flex flex-col h-screen w-full overflow-hidden text-neutral-900" style={{ background: "var(--brand-fog)" }}>
      <TopAlertStrip alerts={alerts} />
      <div className="layout-shell flex flex-1 overflow-hidden">
      {/* Mobile drawer state (only relevant when in Tablet mode) */}
      <MobileSidebar items={items} extraActive={extraActive} user={user} onLogout={async () => { await logout(); navigate("/login"); }} />
      {/* Sidebar — as wide as its longest label (no truncation) */}
      <aside
        className={`w-max min-w-[11rem] flex-shrink-0 flex flex-col ${asideText} ${light ? "border-r border-slate-300" : ""}`}
        style={{ background: asideBg }}
        data-testid="sidebar"
        data-sidebar-style={sbStyle}
        data-sidebar-folded={sbFolded ? "1" : "0"}
      >
        <div className={`h-16 flex items-center px-3 border-b ${divider}`}>
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center text-white font-display font-bold text-sm shrink-0"
              style={{ background: "var(--brand-red)" }}
            >
              L+
            </div>
            <div className="min-w-0">
              <div className={`font-display font-bold text-sm leading-none ${light ? "text-slate-900" : "text-white"}`}>Leprecon+</div>
              <div className={`text-[10px] uppercase tracking-widest mt-1 ${light ? "text-slate-500" : "text-neutral-400"}`}>{t("CRM Inventory")}</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-2 px-1.5 no-scrollbar overflow-y-auto">
          {items.map((it) => (
            <SidebarNavItem key={it.to} item={it} extraActive={extraActive} light={light} folded={sbFolded} />
          ))}
        </nav>
        <div className={`border-t ${divider} p-2 w-0 min-w-full`}>
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-white text-xs shrink-0"
              style={{ background: "var(--brand-red)" }}
              title={user.name}
            >
              {user.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-[13px] font-medium truncate ${light ? "text-slate-900" : "text-white"}`} data-testid="current-user-name">{user.name}</div>
              <div className={`text-[11px] truncate ${light ? "text-slate-500" : "text-neutral-400"}`}>
                {t(ROLE_LABEL[user.role])}
                {user.sub_role && (
                  <span className={`ml-1 ${light ? "text-slate-400" : "text-neutral-500"}`}>· {t(SUB_ROLE_LABEL[user.sub_role])}</span>
                )}
              </div>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="logout-btn"
              className={`p-1.5 rounded-md ${light ? "text-slate-500 hover:text-slate-900 hover:bg-white/70" : "text-neutral-400 hover:text-white hover:bg-white/10"}`}
              title={t("Log out")}
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white flex items-center px-6 justify-between shrink-0 gap-4" style={{ borderBottom: "1px solid var(--brand-line)" }}>
          <div className="flex items-center gap-3 flex-1">
            <button
              type="button"
              data-testid="mobile-menu-btn"
              onClick={() => window.dispatchEvent(new Event("crm-open-mobile-nav"))}
              className="hidden viewport-mode-tablet-only h-9 w-9 rounded-md border border-slate-300 bg-white hover:bg-slate-100 items-center justify-center"
              aria-label="Open navigation"
              style={{ display: "none" }}
            >
              <Menu size={16} />
            </button>
            <HeaderSearch />
          </div>
          <div className="flex items-center gap-4">
            <LanguageToggle />
            <FontSizeToggle />
            <SidebarStyleToggle />
            <ViewportModeToggle />
            <div className="relative" data-testid="alert-bell">
              <Bell size={18} className="text-neutral-600" />
              {alerts.length > 0 && (
                <Badge
                  className="absolute -top-1.5 -right-2 text-white border-none text-[10px] px-1.5 py-0 font-semibold"
                  style={{ background: "var(--brand-red)" }}
                >
                  {alerts.length}
                </Badge>
              )}
            </div>
            <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500" data-testid="role-tag">
              {t(ROLE_LABEL[user.role])}
            </div>
            <button
              type="button"
              data-testid="header-logout-btn"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="h-9 w-9 rounded-md border border-slate-300 bg-white hover:bg-slate-100 items-center justify-center text-slate-700"
              title={`${t("Log out")} · ${t("Switch user")}`}
              style={{ display: "none" }}
            >
              <LogOut size={16} />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
      </div>
    </div>
    </SidebarStyleContext.Provider>
  );
}
